/**
 * TanStack Query hooks for the admin OOC queue (STORY-010-004).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  getOoc,
  listOoc,
  oocQueryKeys,
  transitionOoc,
  type OocDetail,
  type OocListResponse,
  type OocTransitionRequest,
} from "@/features/admin/ooc/api";

const REFETCH_INTERVAL = 30_000;

export function useOocList(
  status: string,
): UseInfiniteQueryResult<InfiniteData<OocListResponse>, Error> {
  return useInfiniteQuery<
    OocListResponse,
    Error,
    InfiniteData<OocListResponse>,
    ReturnType<typeof oocQueryKeys.list>,
    string | null
  >({
    queryKey: oocQueryKeys.list({ status }),
    queryFn: ({ pageParam }) =>
      listOoc({ status, cursor: pageParam, limit: 50 }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
}

export function useOocDetail(id: string | null): UseQueryResult<OocDetail, Error> {
  return useQuery<OocDetail, Error>({
    queryKey: oocQueryKeys.detail(id ?? ""),
    queryFn: () => getOoc(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}

export function useTransitionOoc(): UseMutationResult<
  OocDetail,
  Error,
  { id: string; body: OocTransitionRequest }
> {
  const qc = useQueryClient();
  return useMutation<OocDetail, Error, { id: string; body: OocTransitionRequest }>({
    mutationFn: ({ id, body }) => transitionOoc(id, body),
    onSuccess: async (_data, { id }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "ooc", "list"] }),
        qc.invalidateQueries({ queryKey: oocQueryKeys.detail(id) }),
      ]);
    },
  });
}
